/**
 * Where a photo shared from another app lands.
 *
 * The OS POSTs the file to `/share-target`; the service worker catches that
 * POST, stashes the file, and redirects here as a GET. This screen asks the
 * service worker for the file and lets the inspector say which session it
 * belongs to.
 *
 * Asking which session is the whole screen, and it is not a step that could
 * be skipped. A shared photo arrives with no context at all — the sharing app
 * knows nothing about inspections — so filing it automatically would mean
 * guessing, and evidence attached to the wrong job is worse than evidence
 * that took one extra tap.
 */
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Btn, Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { useCreatePhotoMutation, useCreateVideoMutation, useSessions } from '@kapture/client-core';

export const Route = createFileRoute('/share')({
  component: SharedMediaPage,
  staticData: { mobile: true },
});

interface Shared {
  blob: Blob;
  name: string;
  type: string | null;
}

/**
 * Ask the service worker for the file it stashed.
 *
 * A `MessageChannel` rather than a broadcast: this is a request with one
 * answer, and the worker consumes the file as it replies — so a second
 * listener receiving it would be a second copy of evidence nobody asked for.
 */
async function takeShared(): Promise<Shared | null> {
  const reg = await navigator.serviceWorker?.ready;
  if (!reg?.active) return null;
  return new Promise<Shared | null>((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (e) => resolve((e.data as Shared | null) ?? null);
    reg.active!.postMessage({ type: 'kapture:take-shared' }, [channel.port2]);
    // A worker that never answers must not leave the screen spinning.
    setTimeout(() => resolve(null), 3000);
  });
}

function SharedMediaPage() {
  const navigate = useNavigate();
  const { data: sessions = [] } = useSessions();
  const createPhoto = useCreatePhotoMutation();
  const createVideo = useCreateVideoMutation();

  const [shared, setShared] = useState<Shared | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'empty'>('loading');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      const got = await takeShared();
      setShared(got);
      setState(got ? 'ready' : 'empty');
    })();
  }, []);

  // Only sessions still being worked. A shared photo cannot be filed against
  // a submitted session — the writer gate would refuse the capture, so
  // offering one would be a choice that fails after the tap.
  const open = sessions.filter((s) => s.status === 'draft' || s.status === 'active');

  async function fileInto(sessionId: string) {
    if (!shared || saving) return;
    setSaving(true);
    try {
      const isVideo = (shared.type ?? '').startsWith('video/');
      if (isVideo) {
        await createVideo.mutateAsync({
          sessionId,
          media: shared.blob,
          byteSize: shared.blob.size,
          // A shared clip carries no duration — the recorder measured one, a
          // file does not. Zero says "unknown" rather than inventing a length.
          durationMs: 0,
          mime: shared.type ?? 'video/mp4',
          extension: shared.name.split('.').pop() || 'mp4',
        });
      } else {
        await createPhoto.mutateAsync({
          sessionId,
          media: shared.blob,
          byteSize: shared.blob.size,
          mime: shared.type ?? 'image/jpeg',
          extension: shared.name.split('.').pop() || 'jpg',
        });
      }
      navigate({ to: '/sessions/$sessionId', params: { sessionId } });
    } finally {
      setSaving(false);
    }
  }

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${T.stroke}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Ico d={I.image} size={18} stroke={T.brand} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Shared with Kapture</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {state === 'ready' ? shared?.name : 'Nothing to file'}
          </div>
        </div>
        {state === 'ready' && shared && (
          <Chip tone="neutral" size="sm">
            {Math.max(1, Math.round(shared.blob.size / 1024))} KB
          </Chip>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {state === 'loading' && (
          <div style={{ color: T.textMute, fontSize: 13, fontFamily: T.font }}>Reading…</div>
        )}

        {state === 'empty' && (
          <div style={{ color: T.textDim, fontSize: 13, fontFamily: T.font, lineHeight: 1.6 }}>
            Nothing arrived with this. A shared file is handed over once and
            then cleared, so this page is empty if you have already filed it —
            or if you opened it directly.
          </div>
        )}

        {state === 'ready' && (
          <>
            <div
              style={{
                fontSize: 11,
                color: T.textMute,
                fontFamily: T.font,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 8,
              }}
            >
              File it into
            </div>

            {open.length === 0 ? (
              <div style={{ color: T.textDim, fontSize: 13, fontFamily: T.font, lineHeight: 1.6 }}>
                No open sessions. Start one, then share again — a photo cannot
                be added to a session that has already been submitted.
              </div>
            ) : (
              open.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => void fileInto(s.id)}
                  disabled={saving}
                  style={{
                    width: '100%',
                    marginBottom: 8,
                    padding: 14,
                    borderRadius: 12,
                    border: `1px solid ${T.stroke}`,
                    background: T.surface,
                    color: T.text,
                    textAlign: 'left',
                    fontFamily: T.font,
                    cursor: saving ? 'default' : 'pointer',
                    opacity: saving ? 0.5 : 1,
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{s.title}</div>
                  <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>
                    {s.captureCount} capture{s.captureCount === 1 ? '' : 's'}
                  </div>
                </button>
              ))
            )}
          </>
        )}
      </div>

      <div style={{ padding: 16 }}>
        <Btn tone="light" size="lg" full onClick={() => navigate({ to: '/' })}>
          Back to sessions
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}
